import { Injectable, signal } from '@angular/core';
import { safeLocalStorage } from '../../../../../src/app/utils/safe-storage';
import { PosterDesign, PosterSize } from '../poster/poster.model';
import { LangCode } from './i18n/languages';

/**
 * Posters printed on this browser before signing in. Each carries the claim
 * secret the backend gave out with its link; signing up hands these back so the
 * posters become the new partner's. Sales before that earn nothing.
 */
export interface StoredPoster {
  code: string;
  url: string;
  claimSecret: string;
  cityId: number;
  cityName: string;
  countryCode: string;
  path: string;
  size: PosterSize;
  design: PosterDesign;
  /** Missing on posters stored before posters had a language: English. */
  language?: LangCode;
  businessName: string;
  createdAt: number;
}

const KEY = 'audatours.partner.unclaimedPosters';

@Injectable({ providedIn: 'root' })
export class PosterStore {
  readonly unclaimed = signal<StoredPoster[]>(this.read());

  private read(): StoredPoster[] {
    try {
      const raw = safeLocalStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(p => p?.code && p?.claimSecret) : [];
    } catch {
      return [];
    }
  }

  private write(list: StoredPoster[]): void {
    this.unclaimed.set(list);
    // Storage refused or full: the posters still print, they just cannot be claimed later.
    safeLocalStorage.setItem(KEY, JSON.stringify(list));
  }

  add(poster: StoredPoster): void {
    this.write([poster, ...this.unclaimed().filter(p => p.code !== poster.code)]);
  }

  find(code: string): StoredPoster | undefined {
    return this.unclaimed().find(p => p.code === code);
  }

  claims(): { code: string; secret: string }[] {
    return this.unclaimed().map(p => ({ code: p.code, secret: p.claimSecret }));
  }

  /** Forgets posters the backend has now handed to an account. */
  removeClaimed(codes: string[]): void {
    const done = new Set(codes);
    this.write(this.unclaimed().filter(p => !done.has(p.code)));
  }
}
